import React, { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Check, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Interior Detail",
  "Exterior Detail",
  "Full Detail",
];

const VEHICLE_SIZES = [
  "2 Door Coupe",
  "4 Door Sedan",
  "Small SUV",
  "Large SUV",
  "Full Size Truck",
];

const TIMES = [
  "8:00 AM",
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  service: "",
  vehicle: "",
  vehicleSize: "",
  time: "",
  location: "",
  notes: "",
};

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.phone ||
      !form.service ||
      !form.vehicle ||
      !form.vehicleSize ||
      !form.location ||
      !date ||
      !form.time
    ) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        vehicle: `${form.vehicle} (${form.vehicleSize})`,
        date: format(date, "yyyy-MM-dd"),
      });
      setDone(true);
      toast.success("Booking request received! We'll call you shortly to confirm.");
      setForm(initial);
      setDate(null);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (err?.message?.includes("Network")) {
        toast.error("Network error. Please check your connection and try again.");
      } else {
        toast.error("Something went wrong. Please call 541-622-2960.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="py-24 md:py-32 bg-slate-900 text-white relative overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left info */}
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-blue-400 mb-4">
              Online Booking
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Book your detail<br />in under 60 seconds.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-md">
              Pick a service, a day, and a time that works for you. We'll
              confirm your appointment by phone within a few hours.
            </p>

            <div className="mt-10 space-y-5">
              {[
                "Mobile service, we come to you",
                "Fully insured & locally owned",
                "Pay after the job, cash or Venmo",
                "Free quote, no commitment",
              ].map((p) => (
                <div key={p} className="flex items-start gap-3">
                  <div className="mt-1 h-5 w-5 bg-blue-600 grid place-items-center flex-shrink-0">
                    <Check size={12} strokeWidth={3} />
                  </div>
                  <span className="text-sm text-slate-200">{p}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 p-6 border border-slate-700 bg-slate-800/50">
              <div className="text-[10px] tracking-[0.3em] uppercase text-blue-400 font-bold mb-2">
                Prefer to call?
              </div>
              <a
                href="tel:5416222960"
                className="font-display font-black text-3xl tracking-tight hover:text-blue-400 transition-colors"
                data-testid="booking-call-link"
              >
                541-622-2960
              </a>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="booking-success"
                className="bg-white text-slate-900 p-12 border-l-4 border-blue-600 reveal"
              >
                <div className="h-14 w-14 bg-blue-600 grid place-items-center mb-6">
                  <Check size={28} strokeWidth={3} className="text-white" />
                </div>
                <h3 className="font-display font-black text-3xl tracking-tight">
                  Request received.
                </h3>
                <p className="mt-3 text-slate-600 max-w-md">
                  Thanks! We'll give you a call at the number you provided to
                  confirm your appointment and answer any questions.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="booking-new-button"
                  className="mt-8 inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-600 text-white px-6 py-3 text-sm font-bold transition-colors"
                >
                  Book another service
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="booking-form"
                className="bg-white text-slate-900 p-8 md:p-10"
              >
                <h3 className="font-display font-black text-2xl tracking-tight mb-8">
                  Schedule your appointment
                </h3>

                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Full name *">
                    <Input
                      data-testid="input-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jane Doe"
                      className="rounded-none border-slate-300 focus-visible:ring-blue-600"
                    />
                  </Field>
                  <Field label="Phone *">
                    <Input
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="(541) 555-0123"
                      className="rounded-none border-slate-300 focus-visible:ring-blue-600"
                    />
                  </Field>
                  <Field label="Email (optional)">
                    <Input
                      data-testid="input-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                      className="rounded-none border-slate-300 focus-visible:ring-blue-600"
                    />
                  </Field>
                  <Field label="Vehicle (year, make, model) *">
                    <Input
                      data-testid="input-vehicle"
                      value={form.vehicle}
                      onChange={(e) => update("vehicle", e.target.value)}
                      placeholder="2022 Subaru Outback"
                      className="rounded-none border-slate-300 focus-visible:ring-blue-600"
                    />
                  </Field>
                  <Field label="Vehicle size *">
                    <Select
                      value={form.vehicleSize}
                      onValueChange={(v) => update("vehicleSize", v)}
                    >
                      <SelectTrigger
                        data-testid="select-vehicle-size"
                        className="rounded-none border-slate-300 focus:ring-blue-600"
                      >
                        <SelectValue placeholder="Choose vehicle size" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {VEHICLE_SIZES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Service *">
                    <Select
                      value={form.service}
                      onValueChange={(v) => update("service", v)}
                    >
                      <SelectTrigger
                        data-testid="select-service"
                        className="rounded-none border-slate-300 focus:ring-blue-600"
                      >
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {SERVICES.map((s) => (
                          <SelectItem
                            key={s}
                            value={s}
                            data-testid={`option-service-${s.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Preferred date * (24 hr notice required)">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="button-date-picker"
                          className="w-full inline-flex items-center justify-between border border-slate-300 px-3 py-2 text-sm bg-white hover:border-blue-600 transition-colors h-10"
                        >
                          <span className={date ? "text-slate-900" : "text-slate-400"}>
                            {date ? format(date, "PPP") : "Pick a date"}
                          </span>
                          <CalendarIcon size={16} className="text-slate-500" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 rounded-none"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => {
                            const tomorrow = new Date();
                            tomorrow.setHours(0, 0, 0, 0);
                            tomorrow.setDate(tomorrow.getDate() + 1);
                            return d < tomorrow;
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>
                  <Field label="Preferred time *">
                    <Select
                      value={form.time}
                      onValueChange={(v) => update("time", v)}
                    >
                      <SelectTrigger
                        data-testid="select-time"
                        className="rounded-none border-slate-300 focus:ring-blue-600"
                      >
                        <SelectValue placeholder="Choose a time" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {TIMES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Address *">
                    <Input
                      data-testid="input-location"
                      value={form.location}
                      onChange={(e) => update("location", e.target.value)}
                      placeholder="123 Main St, Medford, OR"
                      className="rounded-none border-slate-300 focus-visible:ring-blue-600"
                    />
                  </Field>
                </div>

                <Field label="Notes (optional)" className="mt-6">
                  <Textarea
                    data-testid="input-notes"
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Pet hair, heavy stains, vehicle access details…"
                    rows={3}
                    className="rounded-none border-slate-300 focus-visible:ring-blue-600"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold px-6 py-4 text-sm transition-all hover:-translate-y-0.5 shadow-md hover:shadow-xl"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending request…
                    </>
                  ) : (
                    "Request booking"
                  )}
                </button>
                <p className="mt-4 text-xs text-slate-500 text-center">
                  By submitting you agree to be contacted about your appointment.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-slate-500 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
